/* Minimal, dependency-free Markdown renderer for admin-authored long-form
   content (blog posts). Supports just enough to structure a post properly
   for readers and SEO: #/##/### headings (with anchor ids, so a sidebar
   table of contents can jump to them), **bold**, [text](url) links,
   "- " bullet lists, "1. " numbered lists, "> " blockquotes, and blank-line
   -separated paragraphs. Deliberately not a full CommonMark implementation —
   anything fancier should use a real library, but this covers what content
   editors actually need without adding a new npm dependency.

   `extractHeadings(text)` walks the same blocks and returns the h1/h2 outline
   so callers (e.g. BlogDetail's "In this post" sidebar) can build a table of
   contents that always matches whatever was actually written, instead of a
   hardcoded list of sections that drifts from the real content.
*/

export function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

export function extractHeadings(text) {
  if (!text || !text.trim()) return [];
  const blocks = text.trim().split(/\n\s*\n/);
  const headings = [];
  for (const block of blocks) {
    const first = (block.split('\n')[0] || '').trim();
    if (first.startsWith('## ')) {
      const t = first.slice(3);
      headings.push({ level: 2, text: t, id: slugify(t) });
    } else if (first.startsWith('# ')) {
      const t = first.slice(2);
      headings.push({ level: 1, text: t, id: slugify(t) });
    }
  }
  return headings;
}

function parseInline(text, keyPrefix = '') {
  const nodes = [];
  const regex = /\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\)/g;
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = regex.exec(text))) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b${i++}`}>{match[1]}</strong>);
    } else {
      const href = match[3];
      const isExternal = /^https?:\/\//.test(href);
      nodes.push(
        <a
          key={`${keyPrefix}-a${i++}`}
          href={href}
          className="text-cyan link-underline"
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >
          {match[2]}
        </a>
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export default function Markdown({ text, className = 'space-y-6' }) {
  if (!text || !text.trim()) return null;
  const blocks = text.trim().split(/\n\s*\n/);

  return (
    <div className={className}>
      {blocks.map((block, i) => {
        const lines = block.split('\n').filter(l => l.trim() !== '');
        const first = lines[0] || '';

        if (first.startsWith('### ')) {
          const t = first.slice(4);
          return <h3 key={i} id={slugify(t)} className="font-display text-2xl text-ink-900 mt-8 scroll-mt-32">{parseInline(t, `h${i}`)}</h3>;
        }
        if (first.startsWith('## ')) {
          const t = first.slice(3);
          return <h2 key={i} id={slugify(t)} className="font-display text-3xl text-ink-900 mt-12 scroll-mt-32">{parseInline(t, `h${i}`)}</h2>;
        }
        if (first.startsWith('# ')) {
          const t = first.slice(2);
          return <h1 key={i} id={slugify(t)} className="font-display text-4xl text-ink-900 mt-12 scroll-mt-32">{parseInline(t, `h${i}`)}</h1>;
        }

        if (lines.every(l => /^>\s?/.test(l.trim()))) {
          return (
            <blockquote key={i} className="mt-8 border-l-4 border-cyan pl-6 italic text-ink-900">
              {lines.map((l, j) => (
                <span key={j}>
                  {parseInline(l.trim().replace(/^>\s?/, ''), `q${i}-${j}`)}
                  {j < lines.length - 1 && <br />}
                </span>
              ))}
            </blockquote>
          );
        }

        if (lines.every(l => /^\d+\.\s+/.test(l.trim()))) {
          return (
            <ol key={i} className="list-decimal space-y-3 pl-6">
              {lines.map((l, j) => <li key={j}>{parseInline(l.trim().replace(/^\d+\.\s+/, ''), `o${i}-${j}`)}</li>)}
            </ol>
          );
        }

        if (lines.every(l => /^[-*]\s+/.test(l.trim()))) {
          return (
            <ul key={i} className="list-disc space-y-2 pl-6">
              {lines.map((l, j) => <li key={j}>{parseInline(l.trim().replace(/^[-*]\s+/, ''), `l${i}-${j}`)}</li>)}
            </ul>
          );
        }

        return (
          <p key={i}>
            {lines.map((l, j) => (
              <span key={j}>
                {parseInline(l, `p${i}-${j}`)}
                {j < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
